import "./footer.css";

import React from "react";

import { BADGES, CONTACT_DETAILS, CREDIT, POLICY_LINKS, QUOTE_LINK, SITE_NAME } from "../../data/content";
import SmartLink from "../general/SmartLink";
import SocialIcons from "../general/SocialIcons";

//Size of the laptop photo, and where its screen is (as % of the photo) - the map sits on the screen, as on Wix
const PHOTO = { width: 1920, height: 1282 };
const SCREEN = { left: 45.3, top: 31.7, width: 28.8, height: 27.1 };
//Below this width the laptop ends up behind the text, so the map shows under the contact details instead
const LAPTOP_MAP_MIN_WIDTH = 1100;

const FooterMap = ({ className, style }) => (
  <div
    className={`site-footer-map ${className}`}
    style={style}
  >
    <iframe
      src={CONTACT_DETAILS.MAP_EMBED}
      title={"Map showing Estia Venue & Events"}
      loading={"lazy"}
      referrerPolicy={"no-referrer-when-downgrade"}
    />
  </div>
);

//Works out where the photo sits once it's scaled to cover the footer (like object-fit: cover), so the map can be
//placed exactly on the laptop screen at any window size
const useCoverBox = (ref) => {
  const [box, setBox] = React.useState(null);

  React.useEffect(() => {
    const el = ref.current;
    if (!el || !window.ResizeObserver) {
      return undefined;
    }
    const observer = new ResizeObserver(() => {
      const w = el.clientWidth;
      const h = el.clientHeight;
      const scale = Math.max(w / PHOTO.width, h / PHOTO.height);
      const width = PHOTO.width * scale;
      const height = PHOTO.height * scale;
      setBox({ footerWidth: w, width, height, left: (w - width) / 2, top: (h - height) / 2 });
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref]);

  return box;
};

//"How to contact us?" over the laptop photo, with the award badges and policy links (as on Wix).
//Wix said "© 2022" - the year now stays current
const Footer = () => {
  const footerRef = React.useRef(null);
  const box = useCoverBox(footerRef);
  const isLaptopMap = box !== null && box.footerWidth >= LAPTOP_MAP_MIN_WIDTH;

  return (
    <footer
      ref={footerRef}
      className={"site-footer"}
    >
      <div
        className={"site-footer-scene"}
        style={box ? { left: box.left, top: box.top, width: box.width, height: box.height } : undefined}
      >
        <img
          src={"/images/footer-bg.webp"}
          alt={""}
          width={PHOTO.width}
          height={PHOTO.height}
          className={"site-footer-bg"}
          loading={"lazy"}
        />
        {isLaptopMap ? (
          <FooterMap
            className={"is-on-laptop"}
            style={{ left: `${SCREEN.left}%`, top: `${SCREEN.top}%`, width: `${SCREEN.width}%`, height: `${SCREEN.height}%` }}
          />
        ) : null}
      </div>

      <div className={"site-footer-inner"}>
        <h2 className={"site-footer-title"}>How to contact us?</h2>
        <p className={"site-footer-details"}>
          Landline: <a href={CONTACT_DETAILS.LANDLINE.link}>{CONTACT_DETAILS.LANDLINE.label}</a>
          <br />
          Cellphone / WhatsApp:
          <br />
          <a href={CONTACT_DETAILS.CELL.link}>{CONTACT_DETAILS.CELL.label}</a>
          <br />
          Email:{" "}
          <a
            href={`mailto:${CONTACT_DETAILS.EMAIL}`}
            className={"is-underlined"}
          >
            {CONTACT_DETAILS.EMAIL}
          </a>
        </p>
        <SocialIcons className={"site-footer-social"} />
        <SmartLink
          href={QUOTE_LINK}
          className={"btn"}
        >
          Get A Quote
        </SmartLink>

        {box !== null && !isLaptopMap ? <FooterMap className={"is-inline"} /> : null}

        <ul className={"site-footer-badges"}>
          {BADGES.map((badge) => (
            <li
              key={badge.src}
              className={badge.isWide ? "is-wide" : ""}
            >
              <a
                href={badge.link}
                target={"_blank"}
                rel={"noopener noreferrer"}
              >
                <img
                  src={badge.src}
                  alt={badge.alt}
                  width={badge.width}
                  height={badge.height}
                  loading={"lazy"}
                />
              </a>
            </li>
          ))}
        </ul>

        <ul className={"site-footer-policies"}>
          {POLICY_LINKS.map((link) => (
            <li key={link.label}>
              <SmartLink
                path={link.path}
                href={link.href}
              >
                {link.label}
              </SmartLink>
            </li>
          ))}
        </ul>
        <p className={"site-footer-credit"}>
          © {new Date().getFullYear()} {SITE_NAME}. Proudly created by{" "}
          <a
            href={CREDIT.link}
            target={"_blank"}
            rel={"noopener noreferrer"}
          >
            {CREDIT.label}
          </a>
        </p>
      </div>
    </footer>
  );
};

export default Footer;
